import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Container,
    Typography,
    Breadcrumbs,
    Link,
    Button,
    IconButton,
    Grid,
    Paper,
    Divider,
    Rating,
    Tabs,
    Tab,
    Avatar,
    TextField,
    Snackbar,
    Alert,
    Stack,
} from "@mui/material";

import { FaHeart, FaRegHeart, FaChevronRight } from "react-icons/fa";

import ProductCardSmall from "../components/ProductCardSmall"

import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";

import { ref, get } from "firebase/database";
import { rtdb } from "../services/firebase";
import { productCategoriasOverlap } from "../utils/productCategories";

import { useAuth } from "../context/AuthContext";

const PageProduto = () => {

    const navigate = useNavigate();

    const { addToCart, cart } = useAuth();
    const { id } = useParams();

    const previousUrlRef = useRef(null);
    const requestSeqRef = useRef(0);

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);

    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedFlavor, setSelectedFlavor] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [tab, setTab] = useState(0);

    const [recommendedProducts, setRecommendedProducts] = useState([]);

    // retorna a variação selecionada
    const selectedVariation = product?.variacoes?.find(
      (v) => v.nome === selectedFlavor
    );

    // estoque disponível do sabor selecionado
    const availableStock = selectedVariation
      ? Number(selectedVariation.quantidade || 0)
      : 0;

    const quantityInCart =
      product && selectedFlavor
        ? cart.find(
            (item) =>
              item.productId === product.id &&
              item.flavor === selectedFlavor
          )?.quantity || 0
        : 0;

    const remainingStock = Math.max(
      availableStock - quantityInCart,
      0
    );

    async function loadRecommendations(currentProduct, requestId) {
        try {
            const snap = await get(ref(rtdb, "products"));

            if (requestId !== requestSeqRef.current) return;
            if (!snap.exists()) return;

            const allProducts = Object.entries(snap.val()).map(([id, data]) => ({
                id,
                ...data,
            }));

            const filtered = allProducts.filter(
                (p) =>
                    p.status === "active" &&
                    p.id !== currentProduct.id &&
                    (productCategoriasOverlap(p, currentProduct) || p.marca === currentProduct.marca)
            );

            const pool =
                filtered.length >= 5
                    ? filtered
                    : allProducts.filter(
                          (p) => p.status === "active" && p.id !== currentProduct.id
                      );

            const shuffled = [...pool].sort(() => 0.5 - Math.random());

            if (requestId !== requestSeqRef.current) return;
            setRecommendedProducts(shuffled.slice(0, 5));
        } catch (err) {
            console.error("Erro ao carregar recomendações", err);
        }
    }

    useEffect(() => {
        if (!id) {
            navigate("/produtos", { replace: true });
            return;
        }

        const requestId = ++requestSeqRef.current;

        // enquanto o novo produto ainda não carregou,
        // a URL precisa refletir a rota real do novo id
        window.history.replaceState(null, "", `/produto/${id}`);

        // limpa imediatamente o estado antigo
        setLoading(true);
        setProduct(null);
        setSelectedImage(null);
        setSelectedFlavor(null);
        setQuantity(1);
        setRecommendedProducts([]);
        setTab(0);

        loadProduct(id, requestId);
    }, [id, navigate]);

    async function loadProduct(productId, requestId) {
        try {
            const snap = await get(ref(rtdb, `products/${productId}`));

            // ignora resposta antiga
            if (requestId !== requestSeqRef.current) return;

            if (!snap.exists()) {
                navigate("/produtos", { replace: true });
                return;
            }

            const data = snap.val();

            if (data.status !== "active") {
                navigate("/produtos", { replace: true });
                return;
            }

            const normalizedProduct = {
                ...data,
                id: productId,
                imagens: data.imagens || [],
                variacoes: data.variacoes || [],
                specs: data.specs || [],
                precos: data.precos || {},
            };

            setProduct(normalizedProduct);
            setSelectedImage(normalizedProduct.imagens[0] || null);

            loadRecommendations(normalizedProduct, requestId);
        } catch (err) {
            console.error(err);
        } finally {
            if (requestId === requestSeqRef.current) {
                setLoading(false);
            }
        }
    }

    if (loading) {
        return (
            <Container sx={{ mt: 10 }}>
                <Typography>Carregando produto...</Typography>
            </Container>
        );
    }

    if (!product) {
        return (
            <Container sx={{ mt: 10 }}>
                <Typography>Produto não encontrado.</Typography>
            </Container>
        );
    }

    const hasStock = remainingStock > 0;
    const hasFlavorSelected = Boolean(selectedFlavor);

    const hasValidQuantity =
      Number.isInteger(quantity) &&
      quantity > 0 &&
      quantity <= remainingStock;

    const canBuy = hasStock && hasFlavorSelected && hasValidQuantity;


    const getUnitPrice = () => {
        if (!product?.precos) return 0;

        const priceString = product.precos.isPromo
            ? product.precos.promo
            : product.precos.normal;

        if (!priceString) return 0;

        return Number(
            priceString
                .replace("R$", "")
                .replace(".", "")
                .replace(",", ".")
                .trim()
        );
    };

    const totalPrice = getUnitPrice() * quantity;

    const formattedTotalPrice = totalPrice.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });

    function handleAddToCart() {
        
        if (!canBuy) {
            setToastMessage("Quantidade indisponível em estoque.");
            setToastOpen(true);
            return;
        }

        addToCart({
            productId: product.id,
            flavor: selectedFlavor,
            quantity,
        });

        setToastMessage("Adicionado ao carrinho!");
        setToastOpen(true);
    }

    return (
        <>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 12 }}>

                {/* BREADCRUMB */}
                <Breadcrumbs
                    separator={<FaChevronRight size={12} color="var(--azul)" />}
                    sx={{ mb: 4, fontSize: 14 }}
                >
                    <Link underline="hover" color="var(--cinza)" href="/">
                        Home
                    </Link>
                    <Link underline="hover" color="var(--cinza)" href="/produtos">
                        Produtos
                    </Link>
                    <Typography color="text.primary">{product.nome}</Typography>
                </Breadcrumbs>

                {/* GRID PRINCIPAL */}
                <Grid
                    container
                    spacing={6}
                    alignItems="flex-start"
                    sx={{ justifyContent: "center" }}
                >

                    {/* GALERIA */}
                    <Grid item xs={12} md={5}>
                        <Paper
                            sx={{
                                width: "100%",
                                height: { xs: 280, sm: 360, md: 450 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "20px",
                                overflow: "hidden",
                                mb: 2,
                                boxShadow: "0 5px 18px rgba(0,0,0,0.12)",
                            }}
                        >
                            <Box
                                component="img"
                                src={selectedImage}
                                alt={product.nome}
                                sx={{
                                    width: "80%",
                                    maxWidth: "100%",
                                    height: "80%",
                                    objectFit: "contain",
                                }}
                            />
                        </Paper>

                        {/* Miniaturas */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            {product.imagens.length > 0 && product.imagens.map((img, index) => (
                                <Paper
                                    key={index}
                                    onClick={() => setSelectedImage(img)}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        padding: 1,
                                        borderRadius: "14px",
                                        cursor: "pointer",
                                        border:
                                            selectedImage === img
                                                ? "2px solid var(--azul)"
                                                : "2px solid transparent",
                                        transition: "0.25s",
                                        "&:hover": {
                                            transform: "scale(1.05)",
                                        },
                                    }}
                                >
                                    <img
                                        src={img}
                                        alt={`preview-${index}`}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                </Paper>
                            ))}
                        </Box>
                    </Grid>

                    {/* INFO */}
                    <Grid
                        item
                        xs={12}
                        md={7}
                        sx={{
                            maxWidth: 560,
                            width: "100%",
                        }}
                    >


                        {/* ID do produto */}
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: "#999",
                                fontWeight: 600,
                                mt: 1,
                                mb: 0.5
                            }}
                        >
                            #{product.id}
                        </Typography>


                        {/* Título */}
                        <Typography sx={{ fontWeight: 700, fontSize: 30, color: "var(--cinza)" }}>
                            {product.nome}
                        </Typography>


                        {/* Preço (com ou sem promoção) */}
                        {product.precos.isPromo ? (
                            <Box sx={{ mt: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontSize: 20,
                                        color: "#888",
                                        textDecoration: "line-through",
                                        fontWeight: 600,
                                    }}
                                >
                                    {product.precos.normal}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: 34,
                                        color: "var(--azul)",
                                        mt: -0.5
                                    }}
                                >
                                    {product.precos.promo}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: 34,
                                    mt: 1,
                                    color: "var(--azul)",
                                }}
                            >
                                {product.precos.normal}
                            </Typography>
                        )}

                        <Typography
                          sx={{
                            fontSize: 15,
                            color: hasFlavorSelected
                              ? hasStock
                                ? "green"
                                : "red"
                              : "#999",
                            mt: 1,
                            fontWeight: 600,
                          }}
                        >
                          {!hasFlavorSelected
                            ? "Selecione um sabor"
                            : hasStock
                            ? `Em estoque (${remainingStock} un.)`
                            : "Esgotado"}
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        {/* Sabores */}
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Disponíveis:</Typography>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {product.variacoes.map((v, index) => {
                              const stock = Number(v.quantidade || 0);
                              const disabled = stock <= 0;

                              return (
                                <Paper
                                  key={index}
                                  onClick={() => {
                                    if (disabled) return;
                                      setSelectedFlavor(v.nome);
                                      setQuantity(1);
                                   }}
                                  sx={{
                                    padding: "8px 16px",
                                    borderRadius: "12px",
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    opacity: disabled ? 0.4 : 1,
                                    backgroundColor:
                                      selectedFlavor === v.nome
                                        ? "var(--azul)"
                                        : "rgba(30,43,78,0.06)",
                                    color:
                                      selectedFlavor === v.nome ? "white" : "var(--cinza)",
                                    fontWeight: selectedFlavor === v.nome ? 700 : 500,
                                    transition: "0.2s",
                                    "&:hover": !disabled
                                      ? { transform: "scale(1.07)" }
                                      : {},
                                  }}
                                >
                                  {v.nome}
                                </Paper>
                              );
                            })}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Quantidade */}
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Quantidade:</Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Button
                                onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                                sx={{
                                    minWidth: 45,
                                    backgroundColor: "rgba(0,0,0,0.05)",
                                    color: "var(--cinza)",
                                    fontSize: 20,
                                    fontWeight: 700,
                                }}
                            >
                                -
                            </Button>

                            <TextField
                                value={quantity}
                                variant="outlined"
                                size="small"
                                sx={{
                                    width: 70,
                                    textAlign: "center",
                                    "& input": { textAlign: "center" },
                                }}
                            />

                            <Button
                                onClick={() =>
                                    quantity < remainingStock && setQuantity(quantity + 1)
                                  }
                                sx={{
                                    minWidth: 45,
                                    backgroundColor: "rgba(0,0,0,0.05)",
                                    color: "var(--cinza)",
                                    fontSize: 20,
                                    fontWeight: 700,
                                }}
                            >
                                +
                            </Button>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* BOTÕES */}
                        <Stack spacing={1.5} sx={{ width: "100%" }}>
                            <Button
                                fullWidth
                                disabled={!canBuy}
                                sx={{
                                    backgroundColor: canBuy ? "var(--azul)" : "#cfd4dc",
                                    color: canBuy ? "white" : "#7a7a7a",
                                    paddingY: 1.7,
                                    fontWeight: 800,
                                    borderRadius: "14px",
                                    fontSize: 16,
                                    cursor: canBuy ? "pointer" : "not-allowed",
                                    "&:hover": canBuy
                                        ? { backgroundColor: "#112040" }
                                        : {},
                                }}
                                onClick={handleAddToCart}
                            >
                                ADICIONAR AO CARRINHO
                            </Button>

                            <Button
                                fullWidth
                                component={RouterLink}
                                to="/carrinho"
                                sx={{
                                    backgroundColor: "var(--azul)",
                                    color: "white",
                                    paddingY: 1.7,
                                    fontWeight: 800,
                                    borderRadius: "14px",
                                    fontSize: 16,
                                    "&:hover": { backgroundColor: "#112040" },
                                }}
                            >
                                IR AO CARRINHO
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>

                {/* TABS */}
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    sx={{ mt: 6 }}
                    textColor="var(--cinza)"
                    indicatorColor="primary"
                >
                    <Tab label="Descrição" />
                    <Tab label="Recomendações" />
                </Tabs>

                <Divider sx={{ mb: 4 }} />

                {/* CONTEÚDO DAS TABS */}
                {tab === 0 && (
                    <Box>
                        <Typography sx={{ fontSize: 16, color: "#444", lineHeight: 1.7 }}>
                            {product.descricao}
                        </Typography>

                        <Typography
                            sx={{ fontWeight: 700, fontSize: 18, mt: 4, mb: 1 }}
                        >
                            Especificações Técnicas
                        </Typography>

                        <Paper sx={{ p: 3, borderRadius: "16px" }}>
                            {product.specs.map((s, i) => (
                                <Box key={i} sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography fontWeight={600}>{s.key}</Typography>
                                    <Typography>{s.value}</Typography>
                                </Box>
                            ))}
                        </Paper>
                    </Box>
                )}

                {tab === 1 && (
                    <Box sx={{ mt: 2 }}>
                        {recommendedProducts.length === 0 ? (
                            <Typography sx={{ color: "#777" }}>
                                Nenhuma recomendação disponível no momento.
                            </Typography>
                        ) : (
                            <Grid
                                container
                                spacing={3}
                                justifyContent="center"
                            >
                                {recommendedProducts.map(p => (
                                    <Grid
                                        item
                                        xs={10}
                                        sm={6}
                                        md={2.4}
                                        key={p.id}
                                    >
                                        <ProductCardSmall product={p} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                )}
            </Container>

            {/* BOTÃO FIXO MOBILE */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    backgroundColor: "#fff",
                    display: { xs: "flex", md: "none" },
                    flexDirection: "column",
                    gap: 1.25,
                    boxShadow: "0 -3px 10px rgba(0,0,0,0.15)",
                    padding: 2,
                    zIndex: 999,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <Box>
                        {product.precos.isPromo && (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "#999",
                                    textDecoration: "line-through",
                                    lineHeight: 1,
                                }}
                            >
                                {product.precos.normal}
                            </Typography>
                        )}

                        <Typography
                            sx={{
                                fontWeight: 800,
                                color: "var(--azul)",
                                fontSize: 18,
                            }}
                        >
                            {formattedTotalPrice}
                        </Typography>
                    </Box>

                    <Button
                        disabled={!canBuy}
                        onClick={handleAddToCart}
                        sx={{
                            backgroundColor: canBuy ? "var(--azul)" : "#cfd4dc",
                            color: canBuy ? "white" : "#7a7a7a",
                            paddingX: 3,
                            paddingY: 1.2,
                            fontWeight: 700,
                            borderRadius: "12px",
                        }}
                    >
                        Adicionar ao Carrinho
                    </Button>
                </Box>

                <Button
                    fullWidth
                    component={RouterLink}
                    to="/carrinho"
                    sx={{
                        backgroundColor: "var(--azul)",
                        color: "white",
                        fontWeight: 700,
                        borderRadius: "12px",
                        py: 1.2,
                        "&:hover": { backgroundColor: "#112040" },
                    }}
                >
                    IR AO CARRINHO
                </Button>
            </Box>
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                sx={{
                    mb: { xs: 10, md: 4 }, // sobe no mobile por causa do botão fixo
                }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    sx={{
                        borderRadius: "14px",
                        fontWeight: 600,
                    }}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PageProduto;